import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import API from '../../services/api';

const URGENCY_ORDER = ['critical', 'high', 'medium', 'low'];

const TAG_HINTS = [
  {
    test: /^meter\.location$/,
    messageType: 'Location tracking failure',
    operationalArea: 'Meter and GPS',
    urgency: 'critical',
    impact: 'Meter distance, live trip tracking, and receipt accuracy may be affected.',
    recommendedAction: 'Contact the driver if they are on a trip, confirm location permission/GPS is enabled, and review the trip fare before closing it.',
  },
  {
    test: /^native\.startTrip$/,
    messageType: 'Native trip runtime failure',
    operationalArea: 'Trip execution',
    urgency: 'critical',
    impact: 'The background trip runtime may not have started, so trip events can be incomplete if the app is backgrounded.',
    recommendedAction: 'Check the active trip for this driver and have the driver keep the app open until the trip is completed or restarted cleanly.',
  },
  {
    test: /^trip\.completionQueue/,
    messageType: 'Trip completion sync issue',
    operationalArea: 'Trip sync',
    urgency: 'high',
    impact: 'A completed trip may need review before the record is trusted.',
    recommendedAction: 'Compare local completion details with the server booking and correct or retry the trip manually if needed.',
  },
  {
    test: /^realtime\.error$/,
    messageType: 'Realtime connection error',
    operationalArea: 'Dispatch connectivity',
    urgency: 'high',
    impact: 'Driver may miss live dispatch updates until the socket reconnects.',
    recommendedAction: 'Check whether the driver is online now and verify network/VPN/connectivity if errors repeat.',
  },
  {
    test: /^auth\.sessionInvalidated$/,
    messageType: 'Driver session ended',
    operationalArea: 'Authentication',
    urgency: 'high',
    impact: 'Driver was signed out or displaced by another device/session.',
    recommendedAction: 'Confirm whether this was expected before troubleshooting dispatch access.',
  },
  {
    test: /^(trip\.offlineStart|trip\.heartbeat|trip\.preCompleteSync|trip\.clear|duty\.queue|realtime\.disconnect)$/,
    messageType: 'Operational warning',
    operationalArea: 'Driver app',
    urgency: 'medium',
    impact: 'The driver app reported a recoverable warning that may need follow-up if it repeats.',
    recommendedAction: 'Look for repeated entries from the same driver and check the related workflow.',
  },
];

function fallbackAnalysis(row) {
  const tag = String(row.tag || '').trim();
  const level = String(row.level || 'info').toLowerCase();
  const hinted = TAG_HINTS.find((hint) => hint.test.test(tag));
  if (hinted) return hinted;
  if (level === 'error') {
    return {
      messageType: tag ? `Unhandled ${tag} error` : 'Unhandled app error',
      operationalArea: 'Driver app',
      urgency: 'high',
      impact: 'The driver app reported an error that may affect operations.',
      recommendedAction: 'Review the message and payload, then check the driver status and related trip if applicable.',
    };
  }
  if (level === 'warn') {
    return {
      messageType: tag ? `Unhandled ${tag} warning` : 'Unhandled app warning',
      operationalArea: 'Driver app',
      urgency: 'medium',
      impact: 'The driver app reported a warning that may need follow-up if it repeats.',
      recommendedAction: 'Look for repeated entries from the same driver and check the related workflow.',
    };
  }
  return {
    messageType: tag ? `Informational ${tag}` : 'Informational diagnostic',
    operationalArea: 'Driver app',
    urgency: 'low',
    impact: 'Informational event only.',
    recommendedAction: 'No action needed.',
  };
}

function contextFromPayload(payload = {}) {
  if (!payload || typeof payload !== 'object') return {};
  const meta = payload._meta || {};
  const app = meta.app || {};
  const device = meta.device || {};
  return {
    bookingId: payload.bookingId || payload.sessionBookingId || payload.op?.bookingId || null,
    queueDepth: payload.queueDepth ?? payload.finalQueueDepth ?? null,
    reason: payload.reason || null,
    source: payload.source || payload.op?.type || null,
    appVersion: app.version ? `${app.version}${app.buildNumber ? ` (${app.buildNumber})` : ''}` : null,
    device: device.model || device.platform ? [device.platform, device.model].filter(Boolean).join(' ') : null,
  };
}

function enrichRow(row) {
  const analysis = row.analysis || fallbackAnalysis(row);
  const context = analysis.context || contextFromPayload(row.payload);
  return {
    ...row,
    analysis: {
      ...analysis,
      context,
      summary: analysis.summary || `${analysis.messageType}.`,
    },
  };
}

function buildSummary(rows) {
  return rows.reduce(
    (acc, row) => {
      const urgency = row.analysis?.urgency || 'low';
      const level = row.level || 'info';
      acc.total += 1;
      acc.byUrgency[urgency] = (acc.byUrgency[urgency] || 0) + 1;
      acc.byLevel[level] = (acc.byLevel[level] || 0) + 1;
      return acc;
    },
    { total: 0, byUrgency: { critical: 0, high: 0, medium: 0, low: 0 }, byLevel: { error: 0, warn: 0, info: 0 } },
  );
}

function urgencyBadgeClass(urgency) {
  if (urgency === 'critical') return 'badge badge-danger';
  if (urgency === 'high') return 'badge badge-warning';
  if (urgency === 'medium') return 'badge badge-info';
  return 'badge badge-success';
}

function formatDate(value) {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
}

function compactContext(context = {}) {
  return [
    context.bookingId ? `Booking ${context.bookingId}` : null,
    context.queueDepth !== null && context.queueDepth !== undefined ? `Queue ${context.queueDepth}` : null,
    context.reason ? `Reason: ${context.reason}` : null,
    context.source ? `Source: ${context.source}` : null,
    context.appVersion ? `App ${context.appVersion}` : null,
    context.device || null,
  ].filter(Boolean);
}

const Diagnostics = () => {
  const [rows, setRows] = useState([]);
  const [apiSummary, setApiSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [driverId, setDriverId] = useState('');
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(100);
  const navigate = useNavigate();

  const enrichedRows = useMemo(() => rows.map(enrichRow), [rows]);
  const summary = useMemo(() => apiSummary || buildSummary(enrichedRows), [apiSummary, enrichedRows]);

  const fetchDiagnostics = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit };
      if (driverId) params.driverId = driverId.trim();
      const res = await API.get('/admin/diagnostics', { params });
      const data = res.data || {};
      setRows(Array.isArray(data.diagnostics) ? data.diagnostics : []);
      setApiSummary(data.summary || null);
    } catch (err) {
      console.error('fetchDiagnostics error', err);
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message || 'Failed to load diagnostics';
      if (status === 401 || status === 403) {
        if (status === 401) {
          navigate('/login');
          setError('Not authenticated. Redirecting to login...');
        } else {
          setError(msg || 'Diagnostics access is disabled on the server.');
        }
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Intentionally wait for an admin request before loading diagnostic data.
  }, []);

  return (
    <AppLayout title="Diagnostics" subtitle="Driver app diagnostics grouped by operational urgency">
      <div className="panel diagnostics-report-panel">
        <div className="panel-header diagnostics-report-header">
          <div>
            <h3>Diagnostics Report</h3>
            <p className="panel-subtitle">Fetch recent warning and error logs, then review each item by urgency and action needed.</p>
          </div>
          <div className="diagnostics-filter-bar">
            <input placeholder="Driver ID" value={driverId} onChange={(e) => setDriverId(e.target.value)} />
            <input
              type="number"
              min="1"
              max="1000"
              placeholder="Limit"
              value={limit}
              onChange={(e) => setLimit(Math.min(1000, Math.max(1, Number(e.target.value || 1))))}
            />
            <button className="btn btn-primary" type="button" onClick={fetchDiagnostics} disabled={loading}>
              Fetch
            </button>
          </div>
        </div>

        <div className="panel-body">
          {loading ? (
            <div className="skeleton" style={{ height: 160 }} />
          ) : error ? (
            <div className="feedback error">{error}</div>
          ) : enrichedRows.length === 0 ? (
            <div className="fleet-alert-empty">No diagnostics loaded. Use Fetch to load recent entries.</div>
          ) : (
            <>
              <div className="diagnostics-summary-grid">
                <div className="diagnostics-summary-card diagnostics-summary-card--total">
                  <span>Total messages</span>
                  <strong>{summary.total}</strong>
                  <em>{summary.byLevel.error || 0} errors / {summary.byLevel.warn || 0} warnings</em>
                </div>
                {URGENCY_ORDER.map((urgency) => (
                  <div key={urgency} className={`diagnostics-summary-card diagnostics-summary-card--${urgency}`}>
                    <span>{urgency}</span>
                    <strong>{summary.byUrgency?.[urgency] || 0}</strong>
                    <em>{urgency === 'critical' || urgency === 'high' ? 'Needs review' : urgency === 'medium' ? 'Watch repeat events' : 'Informational'}</em>
                  </div>
                ))}
              </div>

              <div className="diagnostics-message-list">
                {enrichedRows.map((row, index) => {
                  const analysis = row.analysis;
                  const contextItems = compactContext(analysis.context);
                  const rawPayload = row.payload ? JSON.stringify(row.payload, null, 2) : '';
                  return (
                    <article key={row._id || `${row.driverId}-${row.at}-${index}`} className={`diagnostics-message-card diagnostics-message-card--${analysis.urgency}`}>
                      <div className="diagnostics-message-main">
                        <div className="diagnostics-message-heading">
                          <span className={urgencyBadgeClass(analysis.urgency)}>{analysis.urgency}</span>
                          <span className="badge badge-info">{analysis.operationalArea}</span>
                          <span className="badge badge-info">{row.level || 'info'}</span>
                        </div>
                        <h4>{analysis.messageType}</h4>
                        <p>{analysis.summary}</p>
                        {row.message ? <p className="diagnostics-original-message">{row.message}</p> : null}
                        {contextItems.length ? (
                          <div className="diagnostics-context-chips">
                            {contextItems.map((item) => <span key={item}>{item}</span>)}
                          </div>
                        ) : null}
                        {rawPayload ? (
                          <details className="diagnostics-payload">
                            <summary>Raw payload</summary>
                            <pre>{rawPayload}</pre>
                          </details>
                        ) : null}
                      </div>
                      <div className="diagnostics-message-side">
                        <dl>
                          <dt>Driver</dt>
                          <dd>{row.driverId || '-'}</dd>
                          <dt>Logged</dt>
                          <dd>{formatDate(row.at)}</dd>
                          <dt>Tag</dt>
                          <dd>{row.tag || '-'}</dd>
                        </dl>
                        <div>
                          <strong>Impact</strong>
                          <p>{analysis.impact}</p>
                        </div>
                        <div>
                          <strong>Next step</strong>
                          <p>{analysis.recommendedAction}</p>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default Diagnostics;
